import {ApplicationRef, EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {VERSION} from '../../../environments/version';
import {HttpBackend, HttpClient, HttpHeaders} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../components/confirm-dialog/confirm-dialog.component';
import moment from 'moment';
import {PeriodEnum} from '../../generated/report-client';
import Hotjar from '@hotjar/browser';
import {DATEPICKER_PRESET} from '../../components/datepicker/datepicker.constants';
import {MatDateFormats} from '@angular/material/core';
import {SwUpdate} from "@angular/service-worker";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";

interface ViewSettings {
  environment: string;
  s3Env: string;
  logoFull: string;
  logoCompact: string;
  logoChar: string;
  logoClass: string;
  pageTitle: string;
  ieMessage: string;
  ieMessageDefault: string;
  theme: string;
  filterDateFormat: string;
  dateFormat: string;
  dateFormatWithoutYear: string;
  dateFormatWithWeekday: string;
  dateTimeFormat: string;
  dateTimeFormatWithSeconds: string;
  datePickerShort: string;
  datePickerLong: string;
  dateWeekday: string;
  timeFormat: string;
  timeFormatWithSeconds: string;
  monthYearLabel: string;
  monthYearA11yLabel: string;
  graphReverseYearAndMonth?: boolean;
  adapterFormats?: MatDateFormats;
  hotjarVersion: number;
  hotjarSiteId?: number
  hotjarDebug: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ViewService implements OnDestroy {
  private frequencyToCheck = 60; // in seconds
  private modalOpened = false;
  public dateSeparator = '.';
  public periodSeparator = '–';
  public dateRanges: DATEPICKER_PRESET[] = [];
  public changeDetectionEmitter: EventEmitter<void> = new EventEmitter<void>();
  public latestVersion: string;
  private _destroyed = new Subject<void>();
  private _httpClient: HttpClient;

  constructor(private titleService: Title,
              protected httpBackend: HttpBackend,
              private translateService: TranslateService,
              private dialog: MatDialog,
              private appRef: ApplicationRef,
              private swUpdate: SwUpdate) {
    this._httpClient = new HttpClient(httpBackend);
    titleService.setTitle(this.conf.pageTitle);
    const icon: any = document.querySelector(`link[rel*='icon']`);
    icon.href = this.conf.logoChar;
    if ('serviceWorker' in navigator && location.protocol === 'https:') {
      navigator.serviceWorker.register('/assets/js/doc-sw.js', { scope: '/assets/js/name-forcer/' }).then(() => {
        console.log('Document service worker registered');
      });
      this.validateVersion();
      setInterval(() => {
        this.validateVersion();
      }, this.frequencyToCheck * 1000);
      console.log('swUpdate starting...');
      this.swUpdate.unrecoverable.pipe(takeUntil(this._destroyed)).subscribe(event => {
        console.error('swUpdate got unrecoverable state, reload app manually');
      });
      this.swUpdate.versionUpdates.pipe(takeUntil(this._destroyed)).subscribe(event => {
        if (event.type === 'VERSION_DETECTED') {
          console.log('swUpdate new version detected', event.version);
        }
        if (event.type === 'VERSION_READY') {
          this.outdatedVersionModal();
        }
        if (event.type === 'VERSION_INSTALLATION_FAILED') {
          console.log('swUpdate clearing cache');
          caches.keys().then(keys => {
            keys.forEach(key => caches.delete(key));
          });
        }
      });
    }
    if (this.conf.hotjarSiteId) {
      Hotjar.init(this.conf.hotjarSiteId, this.conf.hotjarVersion, {
        debug: this.conf.hotjarDebug
      });
    }
  }

  public triggerCdr(): void {
    this.changeDetectionEmitter.next();
  }

  private checkForUpdate(): void {
    console.log('swUpdate checking for an update...');
    this.swUpdate.checkForUpdate().then(() => this.outdatedVersionModal()).catch(err => {
      console.log('swUpdate checking encountered an error', err);
    });
  }

  get domainName(): string {
    return window.location.hostname;
  }

  get version(): string {
    return VERSION.version + '.b' + VERSION.buildNumber + '-' + VERSION.hash;
  }

  public validateVersion(): void {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', ['text/plain']);
    const req = this._httpClient.get(`/assets/version?t=` + moment().valueOf(),
      {
        headers: headers,
        responseType: 'text'
      }
    );
    req.subscribe((v) => {
      if (this.version !== v && this.domainName !== 'localhost') {
        this.latestVersion = v;
        if (this.swUpdate.isEnabled) {
          this.checkForUpdate();
        }
      }
    });
  }

  outdatedVersionModal(latest: string = this.latestVersion): void {
    if (this.modalOpened || !this.translateService.store.translations[this.translateService.currentLang]) {
      return;
    }
    this.modalOpened = true;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '60%',
      panelClass: 'xs-popup',
      data: {
        title: this.translateService.instant('general.api_app_version_outdated_title'),
        description: this.translateService.instant('general.api_app_version_outdated_description', { current: this.version, latest: latest }),
        buttonCancel: this.translateService.instant('general.api_app_version_outdated_button_cancel'),
        buttonOk: this.translateService.instant('general.api_app_version_outdated_button_ok')
      }
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.swUpdate.activateUpdate().finally(() => this.reloadApplication());
      }
      this.modalOpened = false;
    });
  }

  public ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  reloadApplication(): void {
    /*const href = cloneDeep(window.location.href).split('?');
    window.location.href = href[0] + (href[1]?.length && href[1].split('&')[1]?.length ? href[1].split('&sw.cacheBust=')[0] + '&' : '?') + 'sw.cacheBust=' + moment().unix()*/
    window.location.reload();
  }

  get conf(): ViewSettings {
    const settings: ViewSettings = {
      environment: '',
      s3Env: 'test',
      logoFull: 'assets/img/svg/logo/piletilevi_logo_full_blue.svg',
      logoCompact: 'assets/img/svg/logo/piletilevi_logo_compact_blue.svg',
      logoChar: 'assets/img/svg/logo/p_logo_small_blue.svg',
      logoClass: '',
      pageTitle: 'Piletilevi müügisüsteem',
      ieMessage: 'general.api_login_ie_message_ee',
      ieMessageDefault: 'This page does not support Internet Explorer. Please use Google Chrome instead.',
      theme: '',
      filterDateFormat: 'YYYY-MM-DD',
      dateFormat: 'DD' + this.dateSeparator + 'MM' + this.dateSeparator + 'YYYY',
      dateFormatWithoutYear: 'DD' + this.dateSeparator + 'MM',
      dateFormatWithWeekday: 'ddd DD' + this.dateSeparator + 'MM' + this.dateSeparator + 'YYYY',
      dateTimeFormat: 'DD' + this.dateSeparator + 'MM' + this.dateSeparator + 'YYYY HH:mm',
      dateTimeFormatWithSeconds: 'DD' + this.dateSeparator + 'MM' + this.dateSeparator + 'YYYY HH:mm:ss',
      datePickerShort: 'MMM D',
      datePickerLong: 'MMM D, YYYY',
      dateWeekday: 'ddd',
      timeFormat: 'HH:mm',
      timeFormatWithSeconds: 'HH:mm:ss',
      monthYearLabel: 'MMM YYYY',
      monthYearA11yLabel: 'MMMM YYYY',
      hotjarVersion: 6,
      hotjarDebug: false
    };
    switch (this.domainName) {
      case 'localhost':
        settings.pageTitle = 'Balticket Development';
        settings.ieMessageDefault = 'See veebileht ei toeta Internet Explorer-it. Palun kasutage Google Chrome.';
        settings.environment = 'LOCAL';
        settings.hotjarSiteId = 3251780;
        settings.hotjarDebug = true;
        break;
      case 'balticket.piletilevi.ee':
      case 'bo3.piletilevi.ee':
      case 'bo3-beta.piletilevi.ee':
        settings.hotjarSiteId = 3192027;
        settings.s3Env = 'prod';
        break;
      case 'bo3.piletilevi.test.helmes.ee':
        settings.environment = 'PRELIVE';
        settings.s3Env = 'prelive';
        break;
      case 'piletilevi.test':
        settings.environment = 'TEST';
        break;
      case 'piletilevi.d3v':
        settings.environment = 'DEV';
        break;
      case 'balticket.bilesuserviss.lv':
      case 'bo3-beta.bilesuserviss.lv':
      case 'bo3.bilesuserviss.lv':
        this.setBilesuservissSettings(settings);
        settings.hotjarSiteId = 3192052;
        settings.s3Env = 'prod';
        break;
      case 'balticket.bilesuserviss.test.helmes.ee':
        this.setBilesuservissSettings(settings);
        settings.environment = 'PRELIVE';
        settings.s3Env = 'prelive';
        break;
      case 'balticket.bilietai.lt':
      case 'bo3-beta.bilietai.lt':
      case 'bo3.bilietai.lt':
        this.setBiletaiSettings(settings);
        settings.hotjarSiteId = 3192049;
        settings.s3Env = 'prod';
        break;
      case 'balticket.bilietai.test.helmes.ee':
        this.setBiletaiSettings(settings);
        settings.environment = 'PRELIVE';
        settings.s3Env = 'prelive';
        break;
      case 'balticket.kvitki.by':
      case 'bo3-beta.kvitki.by':
      case 'bo3.kvitki.by':
        this.setKvitkiSettings(settings);
        settings.hotjarSiteId = 3192056;
        settings.s3Env = 'prod';
        break;
      case 'balticket.kvitki.test.helmes.ee':
        this.setKvitkiSettings(settings);
        settings.environment = 'PRELIVE';
        settings.s3Env = 'prelive';
        break;
    }
    this.updateDateFormats(settings);
    return settings;
  }

  setBilesuservissSettings(settings: ViewSettings): void {
    settings.logoFull = 'assets/img/svg/logo/bilesuserviss_logo_full_blue.svg';
    settings.logoCompact = 'assets/img/svg/logo/bilesuserviss_logo_full_blue.svg';
    settings.logoChar = 'assets/img/svg/logo/b_logo_small_blue.svg';
    settings.logoClass = 'bilesu';
    settings.pageTitle = 'Biļešu Serviss pārdošanas sistēma';
    settings.ieMessage = 'general.api_login_ie_message_lv';
    settings.ieMessageDefault = 'This page does not support Internet Explorer. Please use Google Chrome instead.';
  }

  setBiletaiSettings(settings: ViewSettings): void {
    this.dateSeparator = '-';
    settings.logoFull = 'assets/img/svg/logo/bilietai_logo_full_blue.svg';
    settings.logoCompact = 'assets/img/svg/logo/bilietai_logo_compact_blue.svg';
    settings.logoChar = 'assets/img/svg/logo/b_logo_small_blue.svg';
    settings.pageTitle = 'Bilietai.lt bilietavimo sistema';
    settings.ieMessage = 'general.api_login_ie_message_lt';
    settings.ieMessageDefault = 'This page does not support Internet Explorer. Please use Google Chrome instead.';
    settings.dateFormat = 'YYYY' + this.dateSeparator + 'MM' + this.dateSeparator + 'DD';
    settings.dateFormatWithoutYear = 'MM' + this.dateSeparator + 'DD';
    settings.monthYearLabel = 'YYYY MMM';
    settings.monthYearA11yLabel = 'YYYY MMMM';
    settings.graphReverseYearAndMonth = true;
  }

  updateDateFormats(settings: ViewSettings): void {
    settings.dateTimeFormat = settings.dateFormat + ' ' + settings.timeFormat;
    settings.dateTimeFormatWithSeconds = settings.dateFormat + ' ' + settings.timeFormatWithSeconds;
    settings.dateFormatWithWeekday = settings.dateWeekday + ' ' + settings.dateFormat;
    const regex = new RegExp('\\' + this.dateSeparator + '', 'gi')
    settings.adapterFormats = {
      parse: {
        dateInput: settings.dateFormat.replace(regex, '/'),
      },
      display: {
        dateInput: settings.dateFormat.replace(regex, '/'),
        monthYearLabel: settings.monthYearLabel,
        dateA11yLabel: settings.dateFormat.replace(regex, '/'),
        monthYearA11yLabel: settings.monthYearA11yLabel,
      },
    };
  }

  setKvitkiSettings(settings: ViewSettings): void {
    settings.logoFull = 'assets/img/svg/logo/kbitki_logo_full_blue.svg';
    settings.logoCompact = 'assets/img/svg/logo/kbitki_logo_compact_blue.svg';
    settings.logoChar = 'assets/img/svg/logo/k_logo_small_blue.svg';
    settings.pageTitle = 'Система продаж Kvitki';
    settings.ieMessage = 'general.api_login_ie_message_by';
    settings.ieMessageDefault = 'Эта страница не поддерживает Internet Explorer. Пожалуйста используйте Google Chrome.';
  }

  public getPeriodDate(period: PeriodEnum): string {
    let date = '';
    switch (period) {
      case PeriodEnum.Day:
        date = moment().format(this.conf.dateFormat);
        break;
      case PeriodEnum.Week:
        date = moment().startOf('week').format(this.conf.dateFormat) + ' ' + this.periodSeparator + ' ' + moment().format(this.conf.dateFormat);
        break;
      case PeriodEnum.Month:
        date = moment().startOf('month').format(this.conf.dateFormat) + ' ' + this.periodSeparator + ' ' + moment().format(this.conf.dateFormat);
        break;
    }
    return date;
  }
}
